import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { Group, Input, SearchField } from '@launchpad-ui/components';
import { useQuery } from '@tanstack/react-query';

import registryFallback from '../../public/registry.json';
import { getRecentAppIds } from '../lib/recentApps';
import type { AppEntry } from '../registry';

import { AppTile } from './AppTile';
import { useTheme } from './ThemeProvider';

import styles from './LaunchPad.module.css';

const FALLBACK = registryFallback as AppEntry[];

export function LaunchPad() {
  const { data: registry = FALLBACK } = useQuery({
    queryKey: ['registry'],
    queryFn: () => fetch('/registry.json').then((r) => r.json() as Promise<AppEntry[]>),
    initialData: FALLBACK,
  });

  const [query, setQuery] = useState('');
  const [recentIds] = useState(() => getRecentAppIds());
  const recentApps = recentIds
    .map((id) => registry.find((app) => app.id === id))
    .filter((app): app is AppEntry => app !== undefined);
  const { theme, toggleTheme } = useTheme();

  const filtered = query
    ? registry.filter((app) => {
        const q = query.toLowerCase();
        return app.name.toLowerCase().includes(q) || app.description.toLowerCase().includes(q);
      })
    : null;

  const [focusedIndex, setFocusedIndex] = useState(0);
  const [prevQuery, setPrevQuery] = useState(query);
  if (query !== prevQuery) {
    setPrevQuery(query);
    setFocusedIndex(0);
  }
  const [colCount, setColCount] = useState(4);
  const gridRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  // Captured in the ResizeObserver callback so the post-render effect knows
  // whether focus needs to be restored after tiles remount into new row parents.
  const gridHadFocusRef = useRef(false);

  const attachGrid = useCallback((node: HTMLDivElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    gridRef.current = node;
    if (!node) {
      return;
    }
    const update = () => {
      const cols = window.getComputedStyle(node).gridTemplateColumns;
      const n = cols.trim().split(/\s+/).length;
      gridHadFocusRef.current = node.contains(document.activeElement);
      setColCount(n > 0 ? n : 4);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(node);
    observerRef.current = ro;
  }, []);

  useLayoutEffect(() => {
    if (!gridHadFocusRef.current) {
      return;
    }
    gridHadFocusRef.current = false;
    const tiles = gridRef.current?.querySelectorAll<HTMLElement>('[role="gridcell"] > [tabindex]');
    const index = Math.floor(focusedIndex / colCount) * colCount + (focusedIndex % colCount);
    tiles?.[index]?.focus();
  }, [colCount, focusedIndex]);

  const displayApps = filtered ?? registry;
  const displayCount = displayApps.length;

  const moveFocus = useCallback(
    (next: number) => {
      if (next < 0 || next >= displayCount) {
        return;
      }
      setFocusedIndex(next);
      const tiles = gridRef.current?.querySelectorAll<HTMLElement>('[role="gridcell"] > [tabindex]');
      tiles?.[next]?.focus();
    },
    [displayCount],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const count = displayCount;
      let next = focusedIndex;

      switch (e.key) {
        case 'ArrowRight':
          next = Math.min(focusedIndex + 1, count - 1);
          break;
        case 'ArrowLeft':
          next = Math.max(focusedIndex - 1, 0);
          break;
        case 'ArrowDown':
          if (focusedIndex + colCount < count) {
            next = focusedIndex + colCount;
          }
          break;
        case 'ArrowUp':
          if (focusedIndex - colCount >= 0) {
            next = focusedIndex - colCount;
          }
          break;
        case 'Home':
          next = 0;
          break;
        case 'End':
          next = count - 1;
          break;
        default:
          return;
      }

      e.preventDefault();
      moveFocus(next);
    },
    [focusedIndex, colCount, moveFocus, displayCount],
  );

  const rows: AppEntry[][] = [];
  for (let i = 0; i < displayApps.length; i += colCount) {
    rows.push(displayApps.slice(i, i + colCount));
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.wordmark}>DoubleHelix</h1>
        <button type="button" onClick={toggleTheme} className={styles.themeToggle}>
          {theme === 'light' ? 'Dark mode' : 'Light mode'}
        </button>
      </div>
      <p className={styles.subtitle}>Internal tools launcher</p>
      <SearchField aria-label="Search apps" className={styles.searchField} value={query} onChange={setQuery}>
        <Group>
          <Input placeholder="Search apps..." />
        </Group>
      </SearchField>
      {!filtered && recentApps.length > 0 && (
        <section className={styles.section}>
          <h2 className={styles.sectionHeading}>Recently Visited</h2>
          <div className={styles.grid}>
            {recentApps.map((app, i) => (
              <AppTile key={`recent-${app.id}`} app={app} index={i} />
            ))}
          </div>
        </section>
      )}
      <section className={styles.section}>
        {!filtered && recentApps.length > 0 && <h2 className={styles.sectionHeading}>All Apps</h2>}
        <div ref={attachGrid} className={styles.grid} role="grid" aria-label="Applications" onKeyDown={handleKeyDown}>
          {rows.map((row, rowIndex) => (
            <div key={rowIndex} role="row" style={{ display: 'contents' }}>
              {row.map((app, colIndex) => {
                const index = rowIndex * colCount + colIndex;
                return (
                  <div key={app.id} role="gridcell">
                    <AppTile
                      app={app}
                      index={index}
                      tabIndex={index === focusedIndex ? 0 : -1}
                      onFocus={() => setFocusedIndex(index)}
                    />
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
